import { COLORS } from "../shared/constants";
import { IconBack, IconAlert } from "../shared/icons";
import { formatDuration, formatDate, localISOString } from "../shared/utils";
import { useState, useEffect, useRef, useCallback } from "react";
import SlideToConfirm from "./SlideToConfirm";
import * as api from "../api";

export default // ─── Treatment Session Screen ────────────────────────────────
function TreatmentSessionScreen({ patient, treatment, protocol, currentUser, allSessions, dueReminders, onComplete, onDiscard, onCancel, onOpenAssessment, assessmentScales, onDetermineMT, returnedMT, returnedTxRatio, onClearReturnedMT, deviceSessionId: deviceSessionIdProp }) {
  const isMPNS = protocol?.treatment_type === "MPNS";
  const [phase, setPhase] = useState("PRE"); // PRE, ACTIVE, PAUSED, RESUMING, STOPPED
  const [mtValue, setMtValue] = useState(
    isMPNS ? "" : (treatment.last_mt_value ? String(treatment.last_mt_value) : "")
  );
  const [mtSaved, setMtSaved] = useState(isMPNS ? true : !!treatment.last_mt_value);
  // Initial active dose with titration safety:
  //   - MPNS: use last_final_intensity, fall back to protocol/default.
  //   - Non-MPNS: use min(last_final_intensity, new_max) where new_max
  //     is the dose computed from current MT × ratio. This means a NEW
  //     MT that produces a lower max drops the displayed dose to that
  //     new max; a NEW MT that produces a higher max keeps the prior
  //     (lower) dose. We never auto-escalate above the previous session
  //     and never exceed the new physiologic max.
  const [activeDose, setActiveDose] = useState(() => {
    if (isMPNS) {
      return treatment.last_final_intensity || protocol?.default_intensity_pct || 20;
    }
    const prevFinal = treatment.last_final_intensity;
    if (prevFinal == null) return null;
    const mt = treatment.last_mt_value;
    if (!mt) return prevFinal;  // no MT yet → can't compute cap
    const ratio = (treatment.last_treatment_ratio_pct
                  ?? protocol?.treatment_ratio_pct
                  ?? 120);
    const newMax = Math.min(100, Math.round((mt * ratio) / 100));
    return Math.min(prevFinal, newMax);
  });
  const [deliveredPulses, setDeliveredPulses] = useState("");
  const [elapsedSec, setElapsedSec] = useState(0);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [interruptReason, setInterruptReason] = useState("");
  const [sessionNotes, setSessionNotes] = useState("");
  const [errors, setErrors] = useState({});
  const [stoppedAt, setStoppedAt] = useState(null); // { elapsed, trainsDelivered, intensity }

  // ─── Pause / Resume state ──────────────────────────────────
  // pausedDurationMs accumulates total time spent in PAUSED phase across
  // multiple pause/resume cycles. The timer subtracts this from wall-clock
  // elapsed, so the treatment clock only counts time with MagPro armed and
  // firing.  pauseStartedAtRef tracks the start of the current pause (null
  // when not paused) — only written outside render, safe as a ref.
  const [pausedDurationMs, setPausedDurationMs] = useState(0);
  const pauseStartedAtRef = useRef(null);
  const [pauseLoading, setPauseLoading] = useState(false);
  const [pauseError, setPauseError] = useState(null);

  // ─── Submit guard ──────────────────────────────────────────
  // Prevents double-POST of a session save caused by:
  //   (a) operator double-clicking Save/Discard under lag
  //   (b) timer-driven auto-complete firing at the same moment as
  //       telemetry-driven completion detection
  //   (c) any future path that calls onComplete()
  // Set true on the first call; never reset — the component unmounts right
  // after onComplete().  Backend also has a 10-second dedup guard as
  // defense-in-depth, but this stops the second POST from ever being sent.
  const submittingRef = useRef(false);
  const safeComplete = useCallback((record) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    onComplete(record);
  }, [onComplete]);

  // ─── Device State ───────────────────────────────────────────
  const [deviceConnected, setDeviceConnected] = useState(false);
  const [deviceSimulated, setDeviceSimulated] = useState(false);
  const [telemetry, setTelemetry] = useState(null);
  const [coilMismatch, setCoilMismatch] = useState(false);
  const [mismatchAcknowledged, setMismatchAcknowledged] = useState(false);
  const [deviceLoading, setDeviceLoading] = useState(false);
  const [deviceError, setDeviceError] = useState(null);
  const [deviceEvents, setDeviceEvents] = useState([]);
  const [protocolLoaded, setProtocolLoaded] = useState(null);
  const [deviceArmed, setDeviceArmed] = useState(false);
  const [deviceLocked, setDeviceLocked] = useState(null); // null or { locked_by, locked_for }
  const telemetryInterval = useRef(null);

  // Unique session ID for device lock — passed from App.js, stable across re-renders
  const deviceSessionId = deviceSessionIdProp || useRef(`session-${treatment.id}-${Date.now()}`).current;
  const patientName = patient ? `${patient.first_name} ${patient.last_name}` : "Unknown";

  // Total duration — computed from first principles rather than reading
  // protocol.approx_duration_sec, which can be stale (older seeded protocols
  // had hardcoded values that didn't match the formula, e.g. rTMS original
  // was seeded as 1980s but the actual duration is 2224s = 37:04). Computing
  // here keeps the timer in sync with the MagPro regardless of what's stored
  // on the protocol record. Falls back to the stored value only if a required
  // input is missing.
  //
  // Formula (same as backend _auto_calculate, applies to rTMS, TBS, mPNS):
  //   total = num_trains × (train_pulses / freq) + (num_trains − 1) × iti
  //
  // For TBS, train_pulses = bursts/train and freq = burst frequency, so
  // train_pulses/freq is the burst-train duration. The pulses_in_burst and
  // inter_pulse_interval_ms values control intra-burst timing which is
  // already wrapped inside one 1/freq period — no extra accounting needed.
  const totalDuration = (() => {
    const f = protocol?.frequency_hz;
    const tp = protocol?.train_pulses;
    const nt = protocol?.num_trains;
    const iti = protocol?.inter_train_interval_sec;
    if (f && tp && nt && iti != null && f > 0 && nt > 0) {
      const trainDurSec = tp / f;
      return Math.round(trainDurSec * nt + iti * Math.max(0, nt - 1));
    }
    // Defensive fallback to stored value if protocol is missing inputs.
    return protocol?.approx_duration_sec || 0;
  })();
  const prescribedPulses = protocol?.total_pulses || 0;
  const maxDose = isMPNS ? (protocol?.max_intensity_pct || 50) : 100;
  // Effective Tx %MT ratio for this session.
  // Default chain: just-set-on-MT-screen value > persisted last value on the
  // treatment > protocol default > 120. The MT screen lets the operator pick
  // 120/100/80 or a custom value; the choice persists across sessions via
  // treatment.last_treatment_ratio_pct.
  const effectiveTxRatio = (returnedTxRatio
    ?? treatment?.last_treatment_ratio_pct
    ?? protocol?.treatment_ratio_pct
    ?? 120);
  const treatmentDose = isMPNS
    ? (activeDose !== null ? activeDose : (protocol?.default_intensity_pct || 20))
    : (mtValue ? Math.min(100, Math.round((parseInt(mtValue) * effectiveTxRatio) / 100)) : 0);

  const buildSessionRecord = (status, reason) => ({
    id: `sess${Date.now()}`,
    patient_id: patient.id,
    treatment_assignment_id: treatment.id,
    session_number: sessionNumber,
    session_date: localISOString(sessionStartTime) || localISOString(new Date()),
    session_end: localISOString(new Date()),
    operator_id: currentUser.id,
    protocol_name_snapshot: treatment.protocol_name,
    treatment_type: protocol?.treatment_type || "RTMS",
    prescribed_frequency_hz: protocol?.frequency_hz,
    prescribed_train_pulses: protocol?.train_pulses,
    prescribed_trains: protocol?.num_trains,
    prescribed_iti_sec: protocol?.inter_train_interval_sec,
    prescribed_total_pulses: prescribedPulses,
    delivered_total_pulses: parseInt(deliveredPulses) || 0,
    motor_threshold_value: parseInt(mtValue),
    target_intensity_pct: treatmentDose,
    final_intensity_pct: activeDose || treatmentDose,
    treatment_ratio_pct: effectiveTxRatio,
    treatment_coil: treatment.treatment_coil || protocol?.treatment_coil,
    treatment_spot: treatment.treatment_spot || protocol?.treatment_spot,
    cpt_code: cptCode,
    status: status,
    interruption_reason: reason,
    session_notes: null,
    device_connected: deviceConnected,
    delivered_amplitude_pct: telemetry?.amplitude_pct || null,
    detected_coil: telemetry?.coil_type || null,
    coil_mismatch_acknowledged: mismatchAcknowledged,
    created_at: localISOString(new Date()),
  });

  // CPT logic
  const isFirstEver = allSessions.length === 0;
  const lastMT = allSessions.length > 0 ? allSessions[0]?.motor_threshold_value : null;
  const mtChanged = mtValue && lastMT && parseInt(mtValue) !== lastMT;
  const cptCode = isFirstEver ? "90867" : mtChanged ? "90869" : "90868";

  // Session number — match backend logic exactly (see sessions.py create_session):
  // MAX(session_number) + 1 among sessions that aren't soft-deleted (is_voided=False)
  // AND weren't saved as VOIDED (status != 'VOIDED'). Prefer the cached counter on
  // the treatment when present (it's the backend's authoritative count), fall back
  // to a filter on allSessions. The backend is ultimately authoritative — this is
  // just for display during session entry.
  const sessionNumber = (() => {
    if (treatment.completed_sessions) return treatment.completed_sessions + 1;
    const countedSessions = allSessions.filter((s) =>
      s.treatment_assignment_id === treatment.id &&
      !s.is_voided &&
      s.status !== "VOIDED"
    );
    const maxNum = countedSessions.reduce(
      (m, s) => Math.max(m, s.session_number || 0), 0
    );
    return maxNum + 1;
  })();

  // Calculate trains remaining — train fires first, then ITI gap
  const trainsRemaining = (() => {
    if (!protocol?.num_trains || !protocol?.frequency_hz || !protocol?.train_pulses) return 0;
    const trainDur = protocol.train_pulses / protocol.frequency_hz;
    const iti = protocol.inter_train_interval_sec || 0;
    const cycleDur = trainDur + iti;
    const completedTrains = cycleDur > 0 ? Math.min(protocol.num_trains, Math.floor((elapsedSec + iti) / cycleDur)) : 0;
    return Math.max(0, protocol.num_trains - completedTrains);
  })();
  const remainingSec = Math.max(0, totalDuration - elapsedSec);

  const formatTime = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  // Timer — wall-clock anchored. Computes elapsed as
  //    floor((Date.now() - sessionStartTime - pausedDurationMs) / 1000)
  // Immune to browser setInterval throttling (background tabs, iPad Safari
  // aggressive throttling) because each tick re-reads the wall clock instead
  // of incrementing a counter. Also auto-corrects if a tick is delayed.
  //
  // Subtracts pausedDurationMs so the treatment clock only counts time with
  // the MagPro armed and firing, not time spent paused.
  //
  // Only ticks in ACTIVE phase. PAUSED freezes the display at the last value
  // because this effect exits (phase !== "ACTIVE"), leaving elapsedSec stable.
  // Auto-save when the computed elapsed reaches protocol duration.
  useEffect(() => {
    if (phase !== "ACTIVE" || !sessionStartTime) return;
    const tick = () => {
      const startMs = sessionStartTime.getTime();
      const now = Date.now();
      const elapsed = Math.max(0, Math.floor((now - startMs - pausedDurationMs) / 1000));
      setElapsedSec(elapsed);
    };
    tick(); // immediate update on entering ACTIVE so display isn't stale
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [phase, sessionStartTime, pausedDurationMs]);

  // Auto-save when timer reaches protocol duration
  useEffect(() => {
    if (phase !== "ACTIVE" || totalDuration <= 0) return;
    if (elapsedSec >= totalDuration) {
      const record = buildSessionRecord("COMPLETED", null);
      record.delivered_total_pulses = prescribedPulses;
      record.final_intensity_pct = activeDose || treatmentDose;
      api.deviceReleaseLock(deviceSessionId).catch(() => {});
      safeComplete(record);
    }
  }, [phase, elapsedSec, totalDuration]);

  // ─── Handle MT value returned from MT Determination screen ──
  useEffect(() => {
    if (returnedMT != null && returnedMT > 0) {
      setMtValue(String(returnedMT));
      setMtSaved(true);
      const newDose = Math.min(100, Math.round((returnedMT * effectiveTxRatio) / 100));
      const lastFinal = treatment.last_final_intensity;
      if (lastFinal && lastFinal < newDose) {
        setActiveDose(lastFinal);
      } else {
        setActiveDose(newDose);
      }
      if (onClearReturnedMT) onClearReturnedMT();
    }
  }, [returnedMT]);

  // ─── Device: Check connection on mount ──────────────────────
  useEffect(() => {
    api.deviceStatus().then((status) => {
      setDeviceConnected(status.connected || false);
      setDeviceSimulated(status.simulated || false);
    }).catch(() => {
      setDeviceConnected(false);
    });
  }, []);

  // ─── Device: Acquire lock + load protocol on mount ──────────
  useEffect(() => {
    if (!deviceConnected) return;
    setDeviceLoading(true);

    // Step 1: Acquire device lock
    api.deviceAcquireLock(deviceSessionId, patientName).then((lockResult) => {
      if (lockResult && !lockResult.success) {
        // Device locked by another session
        setDeviceLocked({ locked_by: lockResult.locked_by, locked_for: lockResult.locked_for });
        setDeviceError(`MagPro in use by ${lockResult.locked_by} for ${lockResult.locked_for}`);
        setDeviceLoading(false);
        return;
      }

      // Step 2: Load protocol
      const dose = treatment.last_final_intensity || Math.min(100, Math.round(((treatment.last_mt_value || 50) * effectiveTxRatio) / 100));
      api.deviceLoadParams(treatment.id, dose, deviceSessionId).then((result) => {
        if (!result || result.success) {
          setProtocolLoaded(result || { success: true });
        } else {
          setDeviceError(`Protocol load: ${result.error || "Unknown error"}${result.warnings ? " (" + result.warnings.join(", ") + ")" : ""}`);
        }
        setDeviceLoading(false);
      }).catch((err) => {
        // Transient error — protocol may have loaded successfully on MagPro.
        // Operator verifies on device display before proceeding.
        console.warn("Protocol load response error:", err);
        setProtocolLoaded({ success: true, protocol_name: treatment.protocol_name });
        setDeviceLoading(false);
      });
    }).catch((err) => {
      // Lock request failed (network error, etc.)
      if (err.locked) {
        setDeviceLocked({ locked_by: err.locked_by, locked_for: err.locked_for });
        setDeviceError(`MagPro in use by ${err.locked_by} for ${err.locked_for}`);
      } else {
        setDeviceError(`Device lock failed: ${err.error || err.message || "Unknown error"}`);
      }
      setDeviceLoading(false);
    });
  }, [deviceConnected]); // Only run on mount when connection status is known

  // ─── Device: Release lock on unmount (navigating away without save/discard) ──
  useEffect(() => {
    return () => {
      api.deviceReleaseLock(deviceSessionId).catch(() => {});
    };
  }, []);

  // ─── Device: Telemetry polling ──────
  // During ACTIVE phase, backend uses get_status2() for live data including
  // train Running/Stopped detection. 1-second grace period after send_train.

  // ─── Device: Single telemetry read after protocol load (PRE phase) ──
  useEffect(() => {
    if (!deviceConnected || !protocolLoaded) return;
    // One read to get initial device state after protocol loads
    api.deviceTelemetry(treatment.id).then((t) => {
      setTelemetry(t);
      setDeviceArmed(t.device_enabled || false);
    }).catch(() => {});
  }, [deviceConnected, protocolLoaded]);

  // ─── Device: Poll telemetry during PRE, ACTIVE, PAUSED, and RESUMING ──
  // Polls in PAUSED/RESUMING too so we can detect when the operator physically
  // re-arms the MagPro (drives the "ready to resume" UI). Completion and
  // auto-stop logic is gated on phase === "ACTIVE" so a backend-initiated
  // disarm (from our own pause call) doesn't trigger auto-stop.
  useEffect(() => {
    if (!deviceConnected || !mtSaved) return;
    if (phase !== "PRE" && phase !== "ACTIVE" && phase !== "PAUSED" && phase !== "RESUMING") return;
    const interval = setInterval(() => {
      api.deviceTelemetry(treatment.id).then((t) => {
        setTelemetry(t);
        setDeviceArmed(t.device_enabled || false);

        if (phase !== "ACTIVE") return;

        // Detect protocol completion from MagPro → auto-save
        if (t.protocol_completed) {
          const record = buildSessionRecord("COMPLETED", null);
          record.delivered_total_pulses = prescribedPulses;
          record.final_intensity_pct = t.amplitude_pct || activeDose || treatmentDose;
          api.deviceReleaseLock(deviceSessionId).catch(() => {});
          safeComplete(record);
          return;
        }

        // Detect train stopped (operator pressed Stop or Pause on MagPro)
        if (t.train_running === false && t.treatment_active === false) {
          const trainsDelivered = (protocol?.num_trains || 0) - trainsRemaining;
          const pulsesPerTrain = protocol?.treatment_type === "TBS"
            ? (protocol?.pulses_in_burst || 3) * (protocol?.train_pulses || 0)
            : (protocol?.train_pulses || 0);
          setStoppedAt({
            elapsed: elapsedSec,
            trainsDelivered,
            deliveredPulses: trainsDelivered * pulsesPerTrain,
            intensity: t.amplitude_pct || activeDose || treatmentDose,
          });
          setDeliveredPulses(String(trainsDelivered * pulsesPerTrain));
          setPhase("STOPPED");
          setInterruptReason("");
        }
      }).catch(() => {});
    }, 1500);
    return () => clearInterval(interval);
  }, [deviceConnected, mtSaved, phase, elapsedSec, prescribedPulses, protocol?.num_trains, activeDose, treatmentDose, trainsRemaining, deviceSessionId]);

  // ─── Device: Auto-fill MT value from telemetry (only when telemetry updates) ───
  useEffect(() => {
    if (!deviceConnected || mtSaved) return;
    if (telemetry?.amplitude_pct != null && telemetry.amplitude_pct > 0) {
      setMtValue(String(telemetry.amplitude_pct));
    }
  }, [deviceConnected, mtSaved, telemetry?.amplitude_pct]);

  // ─── Device: Re-send amplitude when MagPro re-arms (resets to 0) ───
  const prevArmedRef = useRef(deviceArmed);
  useEffect(() => {
    if (!deviceConnected) return;
    const wasArmed = prevArmedRef.current;
    prevArmedRef.current = deviceArmed;
    // Detect disabled → enabled transition
    if (!wasArmed && deviceArmed) {
      const dose = mtSaved ? (activeDose !== null ? activeDose : treatmentDose) : (parseInt(mtValue) || 0);
      if (dose > 0) {
        api.deviceSetAmplitude(dose, deviceSessionId).catch(() => {});
      }
    }
    // ─── Auto-stop: detect disarm during ACTIVE treatment ───
    if (wasArmed && !deviceArmed && phase === "ACTIVE") {
      const trainsDelivered = (protocol?.num_trains || 0) - trainsRemaining;
      const pulsesPerTrain = protocol?.treatment_type === "TBS"
        ? (protocol?.pulses_in_burst || 3) * (protocol?.train_pulses || 0)
        : (protocol?.train_pulses || 0);
      setStoppedAt({
        elapsed: elapsedSec,
        trainsDelivered,
        deliveredPulses: trainsDelivered * pulsesPerTrain,
        intensity: activeDose || treatmentDose,
      });
      setDeliveredPulses(String(trainsDelivered * pulsesPerTrain));
      setPhase("STOPPED");
      setInterruptReason("");
      api.deviceStopTreatment(deviceSessionId).catch(() => {});
    }
  }, [deviceArmed, deviceConnected, mtSaved, activeDose, treatmentDose, mtValue, phase, elapsedSec, trainsRemaining]);

  // ─── Device: Sync activeDose from MagPro amplitude during ACTIVE phase ───
  useEffect(() => {
    if (!deviceConnected || phase !== "ACTIVE") return;
    if (telemetry?.amplitude_pct != null && telemetry.amplitude_pct > 0) {
      const deviceAmp = telemetry.amplitude_pct;
      if (deviceAmp !== (activeDose !== null ? activeDose : treatmentDose)) {
        setActiveDose(deviceAmp);
      }
    }
  }, [deviceConnected, phase, telemetry?.amplitude_pct]);

  const handleBegin = async () => {
    if (!mtSaved) { setErrors({ mt: "Complete Motor Threshold Determination first" }); return; }
    setErrors({});
    setDeviceLoading(true);
    setDeviceError(null);

    const dose = activeDose !== null ? activeDose : treatmentDose;

    // Protocol already loaded on mount. Set final amplitude and start.
    // Operator must have physically armed the MagPro before sliding.
    if (deviceConnected) {
      try {
        // Set the final amplitude (may have changed during MT/dose adjustment)
        await api.deviceSetAmplitude(dose, deviceSessionId);
        // Start treatment: sets amplitude + fires train
        const startResult = await api.deviceStartTreatment(deviceSessionId);
        if (startResult && startResult.success === false) {
          setDeviceError(startResult.error || "Start failed");
          setDeviceLoading(false);
          return;
        }
      } catch (err) {
        // Transient error (502, empty response, etc.) — log but proceed.
        // The backend likely succeeded; MagPro may already be running.
        // Operator can verify on the MagPro display.
        console.warn("Start treatment response error (proceeding):", err);
      }
    }

    setSessionStartTime(new Date());
    setPhase("ACTIVE");
    setDeliveredPulses(String(prescribedPulses));
    setDeviceLoading(false);
  };

  // ─── Pause / Resume handlers ──────────────────────────────
  //
  // PAUSE: Freezes the treatment. Disarms the MagPro immediately (backend
  // preserves the loaded protocol). The pause-time clock starts now; the
  // treatment timer freezes at the current elapsed value because the timer
  // effect only ticks during ACTIVE phase.
  //
  // Only pauses that hit the device backend before phase flip will work.
  // We set phase=PAUSED synchronously BEFORE awaiting the pause API call
  // to avoid a race where the auto-complete effect fires between the click
  // and the backend call returning. If the backend call fails, we revert
  // phase back to ACTIVE.
  const handlePause = async () => {
    if (phase !== "ACTIVE") return;
    // Defensive: if the timer has already reached the end, don't allow
    // pause — let auto-complete handle it.
    if (trainsRemaining <= 0) return;

    setPauseError(null);
    setPauseLoading(true);

    // Mark the pause start NOW so elapsed freezes at its current real value.
    // pauseStartedAtRef is a ref because it's read/written outside render.
    pauseStartedAtRef.current = Date.now();

    // Optimistic phase flip — stops the timer and auto-complete immediately.
    setPhase("PAUSED");

    if (deviceConnected) {
      try {
        const result = await api.devicePauseTreatment(deviceSessionId);
        if (result && result.success === false) {
          // Backend failed — revert. The MagPro may or may not be disarmed.
          // Operator should check the device state; safest is to stop.
          setPauseError(result.error || "Pause failed");
          pauseStartedAtRef.current = null;
          setPhase("ACTIVE");
          setPauseLoading(false);
          return;
        }
      } catch (err) {
        // Network/transient — the MagPro state is unknown. Revert phase and
        // surface the error so the operator can decide what to do.
        console.warn("Pause treatment error:", err);
        setPauseError(err?.error || err?.message || "Pause failed — device state unknown");
        pauseStartedAtRef.current = null;
        setPhase("ACTIVE");
        setPauseLoading(false);
        return;
      }
    }
    setPauseLoading(false);
  };

  // RESUME REQUEST: Called by the Resume button in PAUSED phase. Tells the
  // backend to reload the protocol with the remaining-trains count. After
  // this succeeds, phase transitions to RESUMING and the UI prompts the
  // operator to re-arm the MagPro. Actual treatment restart happens in
  // handleResumeConfirm (slide-to-resume after arm).
  const handleResumeRequest = async () => {
    if (phase !== "PAUSED") return;

    // Snapshot the trains remaining at this moment. The frontend derives
    // this from elapsedSec (which is frozen because we're in PAUSED), but
    // re-compute here to be explicit and avoid any stale closure.
    const remaining = trainsRemaining;
    if (remaining <= 0) {
      setPauseError("No trains remaining — end the treatment instead");
      return;
    }

    setPauseError(null);
    setPauseLoading(true);

    if (deviceConnected) {
      try {
        const result = await api.deviceResumeTreatment(deviceSessionId, remaining);
        if (result && result.success === false) {
          setPauseError(result.error || "Resume failed");
          setPauseLoading(false);
          return;
        }
      } catch (err) {
        console.warn("Resume treatment error:", err);
        setPauseError(err?.error || err?.message || "Resume failed");
        setPauseLoading(false);
        return;
      }
    }

    // Protocol reloaded. Wait for operator to physically re-arm.
    setPhase("RESUMING");
    setPauseLoading(false);
  };

  // RESUME CONFIRM: Slide-to-resume from RESUMING phase, after operator has
  // physically re-armed the MagPro. Sends start_treatment, re-anchors the
  // EHR's elapsed clock to match the MagPro's actual state, and returns to
  // ACTIVE. sessionStartTime is NOT reset — only pausedDurationMs grows.
  //
  // ─── Re-anchor math (the important part) ───
  // The MagPro's send_train command always begins with a fresh train (no
  // leading ITI gap). So the moment send_train returns, the device is at
  // the start of train (completed_trains + 1) of the reloaded protocol.
  //
  // In a fresh protocol that ran from elapsed=0 with no pauses, that same
  // instant corresponds to elapsed = completed_trains × cycle_dur. We
  // re-anchor pausedDurationMs so the EHR's wall-clock-derived elapsed
  // equals that target value at the resume instant. Without this the EHR
  // would carry forward the partial-ITI position from before the pause and
  // drift permanently behind the device by however far into the ITI gap
  // (or partial train) the operator paused.
  //
  // Formula: pausedDurationMs = (now - sessionStartTime) - targetElapsedMs
  // This is ABSOLUTE, not incremental — it correctly accumulates across
  // multiple pause/resume cycles by recomputing the offset from scratch
  // each time.
  //
  // Edge cases handled:
  //   - Pause during ITI: timer jumps forward to next-train-start at resume
  //   - Pause during train: timer jumps backward to that-train-start at resume
  //     (the partial train is discarded and re-delivered; matches spec)
  //   - Pause at elapsed=0 / before any train completes: timer resets to 0
  //   - Multiple pause cycles: each re-anchor is correct in absolute terms
  //   - Network ack lag (~50-200ms): elapsed will be slightly behind reality
  //     by that lag, well under 1s — invisible to operator
  const handleResumeConfirm = async () => {
    if (phase !== "RESUMING") return;
    if (!sessionStartTime) {
      // Defensive: should never happen (RESUMING implies a prior ACTIVE),
      // but bail rather than NaN out the timer.
      setPauseError("Cannot resume — session start time unknown");
      return;
    }
    setPauseError(null);
    setPauseLoading(true);

    if (deviceConnected) {
      try {
        const startResult = await api.deviceStartTreatment(deviceSessionId);
        if (startResult && startResult.success === false) {
          setPauseError(startResult.error || "Start failed");
          setPauseLoading(false);
          return;
        }
      } catch (err) {
        // Same lenient behavior as initial start — transient errors likely
        // mean the backend succeeded and the MagPro is firing. Operator
        // verifies on device display.
        console.warn("Start treatment (resume) response error (proceeding):", err);
      }
    }

    // Re-anchor pausedDurationMs so the EHR clock matches "MagPro just
    // started firing train (completed_trains + 1)." See block comment
    // above for derivation. Falls back to simple incremental accumulation
    // if protocol fields are missing (defensive — shouldn't happen).
    const pauseStart = pauseStartedAtRef.current;
    const cycleDurSec = (protocol?.train_pulses && protocol?.frequency_hz)
      ? (protocol.train_pulses / protocol.frequency_hz) + (protocol.inter_train_interval_sec || 0)
      : 0;
    const completedTrains = Math.max(0, (protocol?.num_trains || 0) - trainsRemaining);

    if (cycleDurSec > 0) {
      const targetElapsedMs = completedTrains * cycleDurSec * 1000;
      const newPausedDurationMs = Math.max(
        0,
        Date.now() - sessionStartTime.getTime() - targetElapsedMs
      );
      setPausedDurationMs(newPausedDurationMs);
      // Diagnostic — useful for verifying math against real treatments
      // during initial deployment. Safe to remove later.
      console.log(
        `[Resume re-anchor] completed_trains=${completedTrains} ` +
        `cycle=${cycleDurSec}s target_elapsed=${(targetElapsedMs/1000).toFixed(1)}s ` +
        `new_paused_offset=${(newPausedDurationMs/1000).toFixed(1)}s`
      );
    } else if (pauseStart) {
      // Fallback: if we don't have valid protocol numbers, just accumulate
      // the wall-clock pause duration. Matches old behavior.
      const pauseMs = Date.now() - pauseStart;
      setPausedDurationMs((prev) => prev + Math.max(0, pauseMs));
    }
    pauseStartedAtRef.current = null;

    setPhase("ACTIVE");
    setPauseLoading(false);
  };

  // END FROM PAUSE: Pause → Stop transition. Sends stop_treatment to fully
  // disarm (already disarmed from pause, but this clears backend pause
  // state), then moves to STOPPED so the operator can save or discard.
  const handleEndFromPause = async () => {
    if (phase !== "PAUSED" && phase !== "RESUMING") return;

    const trainsDelivered = (protocol?.num_trains || 0) - trainsRemaining;
    const pulsesPerTrain = protocol?.treatment_type === "TBS"
      ? (protocol?.pulses_in_burst || 3) * (protocol?.train_pulses || 0)
      : (protocol?.train_pulses || 0);
    setStoppedAt({
      elapsed: elapsedSec,
      trainsDelivered,
      deliveredPulses: trainsDelivered * pulsesPerTrain,
      intensity: activeDose || treatmentDose,
    });
    setDeliveredPulses(String(trainsDelivered * pulsesPerTrain));
    setPhase("STOPPED");
    setInterruptReason("");
    pauseStartedAtRef.current = null;

    if (deviceConnected) {
      try {
        await api.deviceStopTreatment(deviceSessionId);
      } catch (err) {
        console.warn("Stop from pause error:", err);
      }
    }
  };

  const handleComplete = () => {
    if (!deliveredPulses) { setErrors({ delivered: "Required" }); return; }
    const record = buildSessionRecord("COMPLETED", null);
    api.deviceReleaseLock(deviceSessionId).catch(() => {});
    safeComplete(record);
  };

  const handleInterruptSave = () => {
    if (!interruptReason) { setErrors({ reason: "Select a reason" }); return; }
    if (!deliveredPulses) { setErrors({ delivered: "Required" }); return; }
    const record = buildSessionRecord("INTERRUPTED", interruptReason);
    record.session_notes = sessionNotes || null;
    api.deviceReleaseLock(deviceSessionId).catch(() => {});
    safeComplete(record);
  };

  const paramRow = (label, value) => (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${COLORS.borderLight}` }}>
      <span style={{ fontSize: "13px", color: COLORS.textSecondary }}>{label}</span>
      <span style={{ fontSize: "13px", fontWeight: "600", color: COLORS.text }}>{value}</span>
    </div>
  );

  return (
    <div style={{ padding: "24px", maxWidth: "900px", margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {phase === "PRE" && (
            <button onClick={() => { api.deviceReleaseLock(deviceSessionId).catch(() => {}); onCancel(); }} style={{ background: "none", border: `1px solid ${COLORS.border}`, borderRadius: "8px", padding: "8px", cursor: "pointer", display: "flex", alignItems: "center", color: COLORS.textSecondary }}><IconBack /></button>
          )}
          <div>
            <h2 style={{ color: COLORS.text, fontSize: "26px", fontWeight: "700", margin: 0 }}>
              {patient.first_name} {patient.last_name}
            </h2>
            <div style={{ fontSize: "14px", color: COLORS.textSecondary, marginTop: "4px" }}>
              {treatment.protocol_name} · Session #{sessionNumber}
            </div>
          </div>
        </div>
        <span style={{ padding: "4px 12px", borderRadius: "8px", fontSize: "12px", fontWeight: "600",
          background: phase === "ACTIVE" ? "#E8F8F0"
            : phase === "STOPPED" ? COLORS.dangerLight
            : phase === "PAUSED" ? COLORS.warningLight
            : phase === "RESUMING" ? "#E8F0FE"
            : COLORS.primaryLight,
          color: phase === "ACTIVE" ? COLORS.accent
            : phase === "STOPPED" ? COLORS.danger
            : phase === "PAUSED" ? COLORS.warning
            : phase === "RESUMING" ? "#1A56DB"
            : COLORS.primary,
        }}>
          {phase === "PRE" ? "Pre-Treatment"
            : phase === "ACTIVE" ? "● In Progress"
            : phase === "PAUSED" ? "❚❚ Paused"
            : phase === "RESUMING" ? "↻ Resuming"
            : phase === "STOPPED" ? "■ Stopped"
            : "—"}
        </span>
      </div>

      {/* Precautions */}
      {patient.precautions && (
        <div style={{ background: COLORS.dangerLight, border: `1px solid ${COLORS.dangerBorder}`, borderRadius: "10px", padding: "12px 16px", marginBottom: "12px", display: "flex", alignItems: "flex-start", gap: "10px" }}>
          <span style={{ color: COLORS.danger, flexShrink: 0, marginTop: "1px" }}><IconAlert /></span>
          <div>
            <span style={{ fontSize: "12px", fontWeight: "700", color: COLORS.danger, textTransform: "uppercase" }}>Precautions</span>
            <p style={{ margin: "4px 0 0", fontSize: "14px", color: COLORS.text }}>{patient.precautions}</p>
          </div>
        </div>
      )}

      {/* Due Reminders on Session Screen */}
      {dueReminders && dueReminders.length > 0 && (
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "20px" }}>
          {dueReminders.map((rem) => {
            const colorMap = {
              yellow: { bg: "#FFF9E6", border: "#F0D060", text: "#8B7000", icon: "⚡" },
              blue: { bg: "#E8F0FE", border: "#7BAAF7", text: "#1A56DB", icon: "📋" },
              orange: { bg: "#FFF3E6", border: "#F0A050", text: "#9A5B00", icon: "🔔" },
            };
            const c = colorMap[rem.color] || colorMap.orange;
            const isAssessment = rem.reminder_type === "ASSESSMENT";
            const isMT = rem.reminder_type === "MT_REDETERMINATION";
            const isClickable = isAssessment || isMT;
            const handleReminderClick = () => {
              if (isAssessment && onOpenAssessment) {
                const scale = (assessmentScales || []).find((s) => s.short_name === rem.label);
                onOpenAssessment({ treatmentId: rem.treatment_assignment_id, scaleId: scale?.id || null });
              } else if (isMT && onDetermineMT) {
                onDetermineMT();
              }
            };
            return (
              <div key={rem.id} onClick={isClickable ? handleReminderClick : undefined}
                style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: "8px", padding: "8px 14px", display: "flex", alignItems: "center", gap: "6px", cursor: isClickable ? "pointer" : "default", transition: "opacity 0.15s" }}
                onMouseEnter={(e) => { if (isClickable) e.currentTarget.style.opacity = "0.8"; }}
                onMouseLeave={(e) => { if (isClickable) e.currentTarget.style.opacity = "1"; }}>
                <span style={{ fontSize: "14px" }}>{c.icon}</span>
                <span style={{ fontSize: "12px", fontWeight: "700", color: c.text }}>
                  {rem.reminder_type === "MT_REDETERMINATION" ? "MT Redetermination Due" : rem.reminder_type === "ASSESSMENT" ? `${rem.label} Due` : rem.label}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
        {/* Left: Parameters */}
        <div style={{ background: COLORS.surface, borderRadius: "12px", border: `1px solid ${COLORS.borderLight}`, padding: "20px" }}>

          {/* MT / Dose — mPNS skips MT entirely, shows dose-only panel */}
          {isMPNS ? (
            <div style={{ background: "#E8F8F0", borderRadius: "10px", border: `1px solid #C8E6D9`, padding: "20px", marginBottom: "20px", minHeight: "150px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
              <div style={{ textAlign: "center", fontSize: "13px", fontWeight: "700", color: COLORS.accent, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Stimulation Intensity
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "16px", padding: "12px 0" }}>
                <button onClick={() => {
                    const newDose = Math.max(1, (activeDose || treatmentDose) - 1);
                    setActiveDose(newDose);
                    if (deviceConnected) api.deviceSetAmplitude(newDose, deviceSessionId).catch(() => {});
                  }}
                  style={{ width: "48px", height: "48px", borderRadius: "12px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: "28px", fontWeight: "700", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>
                  −
                </button>
                <span style={{ fontSize: "56px", fontWeight: "800", color: COLORS.text, minWidth: "100px", textAlign: "center" }}>
                  {activeDose !== null ? activeDose : treatmentDose}%
                </span>
                <button onClick={() => {
                    const newDose = Math.min(maxDose, (activeDose || treatmentDose) + 1);
                    setActiveDose(newDose);
                    if (deviceConnected) api.deviceSetAmplitude(newDose, deviceSessionId).catch(() => {});
                  }}
                  style={{ width: "48px", height: "48px", borderRadius: "12px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: "28px", fontWeight: "700", cursor: (activeDose || treatmentDose) >= maxDose ? "not-allowed" : "pointer", opacity: (activeDose || treatmentDose) >= maxDose ? 0.3 : 1, display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>
                  +
                </button>
              </div>
              <div style={{ textAlign: "center", fontSize: "12px", color: COLORS.textSecondary }}>
                Default: {protocol?.default_intensity_pct || 20}% · Max: {maxDose}% · No MT required
              </div>
            </div>
          ) : !mtSaved ? (
            <div style={{ background: "#1A1F2E", borderRadius: "10px", padding: "24px", marginBottom: "20px", textAlign: "center" }}>
              <div style={{ fontSize: "13px", fontWeight: "700", color: "#7BAAF7", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "8px" }}>
                Motor Threshold
              </div>
              <div style={{ fontSize: "14px", color: "rgba(255,255,255,0.5)", marginBottom: "16px" }}>
                Determine motor threshold before starting treatment.
              </div>
              <button onClick={onDetermineMT}
                style={{ height: "48px", padding: "0 32px", borderRadius: "10px", border: "none", background: "#7BAAF7", color: "#FFF", fontSize: "15px", fontWeight: "700", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "8px" }}>
                ⚡ Determine MT
              </button>
            </div>
          ) : (
            <>
            <div style={{ background: "#E8F8F0", borderRadius: "10px", border: `1px solid #C8E6D9`, padding: "20px", marginBottom: "12px", minHeight: "190px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
              <div style={{ textAlign: "center", fontSize: "13px", fontWeight: "700", color: COLORS.accent, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Treatment Dose
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "16px", padding: "12px 0" }}>
                <button onClick={() => {
                    const newDose = Math.max(1, ((activeDose !== null ? activeDose : treatmentDose)) - 1);
                    setActiveDose(newDose);
                    if (deviceConnected) api.deviceSetAmplitude(newDose, deviceSessionId).catch(() => {});
                  }}
                  style={{ width: "48px", height: "48px", borderRadius: "12px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: "28px", fontWeight: "700", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>
                  −
                </button>
                <span style={{ fontSize: "56px", fontWeight: "800", color: (activeDose !== null && activeDose < treatmentDose) ? "#1A56DB" : COLORS.text, minWidth: "120px", textAlign: "center", display: "inline-flex", alignItems: "baseline", justifyContent: "center" }}>
                  <input type="text" inputMode="numeric" pattern="[0-9]*"
                    value={activeDose !== null ? String(activeDose) : String(treatmentDose)}
                    onChange={(e) => {
                      const v = e.target.value.replace(/[^0-9]/g, "");
                      if (v === "") { setActiveDose(0); return; }
                      const num = parseInt(v);
                      if (num >= 0 && num <= treatmentDose) setActiveDose(num);
                    }}
                    onBlur={() => {
                      const dose = activeDose !== null ? activeDose : treatmentDose;
                      if (deviceConnected && dose > 0) {
                        api.deviceSetAmplitude(dose, deviceSessionId).catch(() => {});
                      }
                    }}
                    style={{ width: `${Math.max(2, String(activeDose !== null ? activeDose : treatmentDose).length + 1) * 0.55}em`, fontSize: "inherit", fontWeight: "inherit", lineHeight: 1, textAlign: "right", color: "inherit", background: "transparent", border: "none", outline: "none", padding: 0, margin: 0 }}
                    placeholder="—" />%
                </span>
                <button onClick={() => {
                    const currentDose = activeDose !== null ? activeDose : treatmentDose;
                    if (currentDose >= treatmentDose) return;
                    const newDose = Math.min(treatmentDose, currentDose + 1);
                    setActiveDose(newDose);
                    if (deviceConnected) api.deviceSetAmplitude(newDose, deviceSessionId).catch(() => {});
                  }}
                  style={{ width: "48px", height: "48px", borderRadius: "12px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: "28px", fontWeight: "700", cursor: (activeDose !== null && activeDose >= treatmentDose) ? "not-allowed" : "pointer", opacity: (activeDose !== null && activeDose >= treatmentDose) ? 0.3 : 1, display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>
                  +
                </button>
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "16px" }}>
                <span style={{ fontSize: "13px", color: COLORS.textSecondary }}>
                  {deviceConnected && telemetry?.amplitude_pct != null
                    ? `Device: ${telemetry.amplitude_pct}%`
                    : `Target: ${treatmentDose}%`}
                </span>
              </div>
            </div>
            {/* MT info + Redo MT — below the dose box */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "16px", marginBottom: "20px" }}>
              <span style={{ fontSize: "13px", fontWeight: "600", color: COLORS.textSecondary }}>MT: {mtValue}%</span>
              <span style={{ fontSize: "13px", color: COLORS.textSecondary }}>
                {mtValue} × {effectiveTxRatio}% = {treatmentDose}%
              </span>
              {phase === "PRE" && (
                <button onClick={onDetermineMT}
                  style={{ height: "28px", padding: "0 12px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.primary, fontSize: "11px", fontWeight: "600", cursor: "pointer" }}>
                  Redo MT
                </button>
              )}
            </div>
            </>
          )}

          {/* ─── Device Lock Warning ─── */}
          {deviceLocked && (
            <div style={{ background: "#FEF3CD", borderRadius: "12px", border: "1px solid #F0C36D", padding: "20px", marginBottom: "12px", textAlign: "center" }}>
              <div style={{ fontSize: "16px", fontWeight: "700", color: "#856404", marginBottom: "8px" }}>MagPro In Use</div>
              <div style={{ fontSize: "14px", color: "#856404", marginBottom: "4px" }}>
                Treatment in progress for <strong>{deviceLocked.locked_for}</strong>
              </div>
              <div style={{ fontSize: "13px", color: "#997A0A" }}>
                Operator: {deviceLocked.locked_by}
              </div>
              <div style={{ fontSize: "12px", color: "#997A0A", marginTop: "12px" }}>
                The device will be available when the current session is saved or discarded.
              </div>
            </div>
          )}

          {/* ─── Controls: Slide-to-begin / Stop / Keep+Discard ─── */}
          <div style={{ background: COLORS.surface, borderRadius: "12px", border: `1px solid ${COLORS.borderLight}`, padding: "20px", textAlign: "center" }}>
            {phase === "PRE" && (
              <div>
                {mtSaved ? (
                  <div>
                    {!deviceConnected ? (
                      /* Not connected — show connect button */
                      <div>
                        <div style={{ fontSize: "13px", color: COLORS.textSecondary, marginBottom: "16px" }}>
                          MagPro is not connected. Connect to continue.
                        </div>
                        <div style={{ display: "flex", justifyContent: "center" }}>
                          <button onClick={async () => {
                              setDeviceLoading(true);
                              try {
                                const result = await api.deviceConnect();
                                setDeviceConnected(result.success);
                                setDeviceSimulated(result.simulated || false);
                                setDeviceError(result.success ? null : result.message);
                              } catch (err) {
                                setDeviceError(err.error || "Connection failed");
                              }
                              setDeviceLoading(false);
                            }}
                            disabled={deviceLoading}
                            style={{ width: "300px", height: "52px", borderRadius: "26px", border: "none", background: "#3498DB", color: "#FFF", fontSize: "15px", fontWeight: "700", cursor: deviceLoading ? "wait" : "pointer" }}>
                            {deviceLoading ? "Connecting…" : "Press to Connect MagPro"}
                          </button>
                        </div>
                      </div>
                    ) : deviceLoading ? (
                      <div style={{ fontSize: "14px", color: COLORS.primary, fontWeight: "600", padding: "16px" }}>
                        Loading parameters to device…
                      </div>
                    ) : deviceConnected && !deviceArmed ? (
                      /* Waiting for operator to arm/enable MagPro */
                      <div>
                        <div style={{ fontSize: "13px", color: COLORS.textSecondary, marginBottom: "16px" }}>
                          MT saved. Enable the MagPro to continue.
                        </div>
                        <div style={{ display: "flex", justifyContent: "center" }}>
                          <div style={{
                            width: "300px", height: "52px", borderRadius: "26px",
                            background: COLORS.warning, display: "flex", alignItems: "center",
                            justifyContent: "center", userSelect: "none",
                          }}>
                            <span style={{ color: "#FFF", fontSize: "13px", fontWeight: "700", letterSpacing: "0.3px" }}>
                              Enable MagPro to Start
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* Connected and armed — show live slider */
                      <div>
                        <div style={{ fontSize: "13px", color: COLORS.textSecondary, marginBottom: "16px" }}>
                          MagPro enabled. Slide to start treatment.
                        </div>
                        <div style={{ display: "flex", justifyContent: "center" }}>
                          <SlideToConfirm label="Start" color={COLORS.accent} onConfirm={handleBegin} />
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ fontSize: "13px", color: COLORS.textSecondary }}>
                    Complete Motor Threshold Determination to begin treatment.
                  </div>
                )}
              </div>
            )}

            {phase === "ACTIVE" && (
              <div>
                <div style={{ fontSize: "13px", color: COLORS.accent, fontWeight: "600", marginBottom: "16px", textAlign: "center" }}>● Treatment in progress</div>
                <div style={{ display: "flex", justifyContent: "center", gap: "12px" }}>
                  <button onClick={handlePause}
                    disabled={pauseLoading || trainsRemaining <= 0}
                    style={{ height: "48px", padding: "0 28px", borderRadius: "10px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: "15px", fontWeight: "700", cursor: (pauseLoading || trainsRemaining <= 0) ? "not-allowed" : "pointer", opacity: (pauseLoading || trainsRemaining <= 0) ? 0.5 : 1, display: "flex", alignItems: "center", gap: "8px" }}>
                    {pauseLoading ? "Pausing…" : "❚❚ Pause"}
                  </button>
                  <button onClick={async () => {
                      const trainsDelivered = (protocol?.num_trains || 0) - trainsRemaining;
                      const pulsesPerTrain = protocol?.treatment_type === "TBS"
                        ? (protocol?.pulses_in_burst || 3) * (protocol?.train_pulses || 0)
                        : (protocol?.train_pulses || 0);
                      setStoppedAt({
                        elapsed: elapsedSec,
                        trainsDelivered,
                        deliveredPulses: trainsDelivered * pulsesPerTrain,
                        intensity: activeDose || treatmentDose,
                      });
                      setDeliveredPulses(String(trainsDelivered * pulsesPerTrain));
                      setPhase("STOPPED");
                      if (deviceConnected) {
                        try {
                          await api.deviceStopTreatment(deviceSessionId);
                          const delivered = await api.deviceDeliveredParams();
                          if (delivered.success && delivered.amplitude_pct != null) {
                            setStoppedAt(prev => ({ ...prev, intensity: delivered.amplitude_pct }));
                          }
                          await api.deviceDisarm(deviceSessionId).catch(() => {});
                        } catch (err) {
                          console.warn("Device read-back error:", err);
                        }
                      }
                    }}
                    style={{ height: "48px", padding: "0 28px", borderRadius: "10px", border: "none", background: COLORS.danger, color: "#FFF", fontSize: "15px", fontWeight: "700", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" }}>
                    ■ Stop
                  </button>
                </div>
                {pauseError && (
                  <div style={{ marginTop: "12px", padding: "8px 12px", background: COLORS.dangerLight, border: `1px solid ${COLORS.dangerBorder}`, borderRadius: "8px", fontSize: "12px", color: COLORS.danger, textAlign: "center" }}>
                    {pauseError}
                  </div>
                )}
              </div>
            )}

            {phase === "PAUSED" && (
              <div>
                <div style={{ padding: "14px", background: COLORS.warningLight, border: `1px solid ${COLORS.warningBorder}`, borderRadius: "10px", marginBottom: "14px" }}>
                  <div style={{ fontSize: "14px", fontWeight: "700", color: COLORS.warning, marginBottom: "8px", textAlign: "center" }}>
                    ❚❚ Treatment Paused
                  </div>
                  <div style={{ fontSize: "12px", color: COLORS.textSecondary, textAlign: "center", marginBottom: "4px" }}>
                    MagPro is disarmed. Protocol is held in memory.
                  </div>
                  <div style={{ fontSize: "12px", color: COLORS.textSecondary, textAlign: "center" }}>
                    {trainsRemaining} of {protocol?.num_trains || 0} trains remaining ·
                    elapsed {formatTime(elapsedSec)}
                  </div>
                </div>
                <div style={{ display: "flex", justifyContent: "center", gap: "12px" }}>
                  <button onClick={handleResumeRequest}
                    disabled={pauseLoading || trainsRemaining <= 0}
                    style={{ height: "48px", padding: "0 28px", borderRadius: "10px", border: "none", background: COLORS.accent, color: "#FFF", fontSize: "15px", fontWeight: "700", cursor: (pauseLoading || trainsRemaining <= 0) ? "not-allowed" : "pointer", opacity: (pauseLoading || trainsRemaining <= 0) ? 0.5 : 1 }}>
                    {pauseLoading ? "Preparing…" : "▶ Resume"}
                  </button>
                  <button onClick={handleEndFromPause}
                    disabled={pauseLoading}
                    style={{ height: "48px", padding: "0 28px", borderRadius: "10px", border: "none", background: COLORS.danger, color: "#FFF", fontSize: "15px", fontWeight: "700", cursor: pauseLoading ? "not-allowed" : "pointer", opacity: pauseLoading ? 0.5 : 1 }}>
                    ■ End
                  </button>
                </div>
                {pauseError && (
                  <div style={{ marginTop: "12px", padding: "8px 12px", background: COLORS.dangerLight, border: `1px solid ${COLORS.dangerBorder}`, borderRadius: "8px", fontSize: "12px", color: COLORS.danger, textAlign: "center" }}>
                    {pauseError}
                  </div>
                )}
              </div>
            )}

            {phase === "RESUMING" && (
              <div>
                <div style={{ padding: "14px", background: "#E8F0FE", border: `1px solid #7BAAF7`, borderRadius: "10px", marginBottom: "14px" }}>
                  <div style={{ fontSize: "14px", fontWeight: "700", color: "#1A56DB", marginBottom: "8px", textAlign: "center" }}>
                    Ready to Resume
                  </div>
                  <div style={{ fontSize: "12px", color: COLORS.textSecondary, textAlign: "center", marginBottom: "4px" }}>
                    Protocol reloaded with {trainsRemaining} trains remaining.
                  </div>
                  <div style={{ fontSize: "12px", color: COLORS.textSecondary, textAlign: "center" }}>
                    {deviceArmed
                      ? "MagPro armed. Slide to resume treatment."
                      : "Enable the MagPro to continue."}
                  </div>
                </div>
                {deviceArmed ? (
                  <div style={{ display: "flex", justifyContent: "center", gap: "12px" }}>
                    <SlideToConfirm label="Resume" color={COLORS.accent} onConfirm={handleResumeConfirm} />
                  </div>
                ) : (
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <div style={{
                      width: "300px", height: "52px", borderRadius: "26px",
                      background: COLORS.warning, display: "flex", alignItems: "center",
                      justifyContent: "center", userSelect: "none",
                    }}>
                      <span style={{ color: "#FFF", fontSize: "13px", fontWeight: "700", letterSpacing: "0.3px" }}>
                        Enable MagPro to Resume
                      </span>
                    </div>
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "center", marginTop: "12px" }}>
                  <button onClick={handleEndFromPause}
                    disabled={pauseLoading}
                    style={{ height: "36px", padding: "0 20px", borderRadius: "8px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.textSecondary, fontSize: "13px", fontWeight: "600", cursor: pauseLoading ? "not-allowed" : "pointer", opacity: pauseLoading ? 0.5 : 1 }}>
                    End without resuming
                  </button>
                </div>
                {pauseError && (
                  <div style={{ marginTop: "12px", padding: "8px 12px", background: COLORS.dangerLight, border: `1px solid ${COLORS.dangerBorder}`, borderRadius: "8px", fontSize: "12px", color: COLORS.danger, textAlign: "center" }}>
                    {pauseError}
                  </div>
                )}
              </div>
            )}

            {phase === "STOPPED" && (
              <div style={{ textAlign: "left" }}>
                <div style={{ padding: "14px", background: COLORS.warningLight, border: `1px solid ${COLORS.warningBorder}`, borderRadius: "10px", marginBottom: "14px" }}>
                  <div style={{ fontSize: "14px", fontWeight: "600", color: COLORS.warning, marginBottom: "12px" }}>Treatment Stopped</div>

                  {stoppedAt && (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px", marginBottom: "14px" }}>
                      <div style={{ padding: "10px", background: COLORS.surface, borderRadius: "8px", textAlign: "center" }}>
                        <div style={{ fontSize: "10px", fontWeight: "600", color: COLORS.textSecondary, textTransform: "uppercase", marginBottom: "4px" }}>Time Elapsed</div>
                        <div style={{ fontSize: "18px", fontWeight: "700", color: COLORS.text }}>{formatTime(stoppedAt.elapsed)}</div>
                      </div>
                      <div style={{ padding: "10px", background: COLORS.surface, borderRadius: "8px", textAlign: "center" }}>
                        <div style={{ fontSize: "10px", fontWeight: "600", color: COLORS.textSecondary, textTransform: "uppercase", marginBottom: "4px" }}>Trains Delivered</div>
                        <div style={{ fontSize: "18px", fontWeight: "700", color: COLORS.text }}>{stoppedAt.trainsDelivered} / {protocol?.num_trains || 0}</div>
                      </div>
                      <div style={{ padding: "10px", background: COLORS.surface, borderRadius: "8px", textAlign: "center" }}>
                        <div style={{ fontSize: "10px", fontWeight: "600", color: COLORS.textSecondary, textTransform: "uppercase", marginBottom: "4px" }}>Intensity</div>
                        <div style={{ fontSize: "18px", fontWeight: "700", color: COLORS.text }}>{stoppedAt.intensity}%</div>
                      </div>
                    </div>
                  )}

                  <div style={{ marginBottom: "12px" }}>
                    <label style={{ fontSize: "12px", fontWeight: "600", color: COLORS.textSecondary, marginBottom: "4px", display: "block" }}>Reason for stopping</label>
                    <select style={{ width: "100%", height: "40px", padding: "0 10px", borderRadius: "8px", border: `1px solid ${COLORS.border}`, fontSize: "13px", boxSizing: "border-box" }}
                      value={interruptReason} onChange={(e) => setInterruptReason(e.target.value)}>
                      <option value="">Select reason…</option>
                      <option value="COMPLETED">Treatment completed normally</option>
                      <option value="PATIENT_DISCOMFORT">Patient discomfort</option>
                      <option value="ADVERSE_EVENT">Adverse event</option>
                      <option value="EQUIPMENT_FAULT">Equipment fault</option>
                      <option value="COIL_OVERHEAT">Coil overheat</option>
                      <option value="SCHEDULING">Scheduling</option>
                      <option value="OTHER">Other</option>
                    </select>
                  </div>
                  <div style={{ marginBottom: "12px" }}>
                    <label style={{ fontSize: "12px", fontWeight: "600", color: COLORS.textSecondary, marginBottom: "4px", display: "block" }}>Session Notes (optional)</label>
                    <textarea style={{ width: "100%", minHeight: "60px", padding: "10px", borderRadius: "8px", border: `1px solid ${COLORS.border}`, fontSize: "13px", resize: "vertical", boxSizing: "border-box" }}
                      value={sessionNotes} onChange={(e) => setSessionNotes(e.target.value)} placeholder="Add any notes about this session…" />
                  </div>
                </div>

                <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
                  <button onClick={() => {
                      const status = interruptReason === "COMPLETED" || !interruptReason ? "COMPLETED" : "INTERRUPTED";
                      const record = buildSessionRecord(status, status === "INTERRUPTED" ? interruptReason : null);
                      record.session_notes = sessionNotes || null;
                      record.delivered_total_pulses = stoppedAt?.deliveredPulses || parseInt(deliveredPulses) || 0;
                      record.final_intensity_pct = stoppedAt?.intensity || activeDose || treatmentDose;
                      api.deviceReleaseLock(deviceSessionId).catch(() => {});
                      safeComplete(record);
                    }}
                    disabled={submittingRef.current}
                    style={{ height: "48px", padding: "0 28px", borderRadius: "10px", border: "none", background: COLORS.accent, color: "#FFF", fontSize: "14px", fontWeight: "700", cursor: submittingRef.current ? "not-allowed" : "pointer", opacity: submittingRef.current ? 0.6 : 1 }}>
                    ✓ Save
                  </button>
                  <button onClick={() => {
                      const record = buildSessionRecord("VOIDED", interruptReason || "VOIDED");
                      record.session_notes = sessionNotes || null;
                      record.delivered_total_pulses = stoppedAt?.deliveredPulses || parseInt(deliveredPulses) || 0;
                      record.final_intensity_pct = stoppedAt?.intensity || activeDose || treatmentDose;
                      api.deviceReleaseLock(deviceSessionId).catch(() => {});
                      safeComplete(record);
                    }}
                    disabled={submittingRef.current}
                    style={{ height: "48px", padding: "0 28px", borderRadius: "10px", border: "none", background: COLORS.danger, color: "#FFF", fontSize: "14px", fontWeight: "700", cursor: submittingRef.current ? "not-allowed" : "pointer", opacity: submittingRef.current ? 0.6 : 1 }}>
                    ✕ Discard
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Session count — always visible */}
          <div style={{ background: COLORS.surface, borderRadius: "12px", border: `1px solid ${COLORS.borderLight}`, padding: "14px 20px", marginTop: "12px", textAlign: "center" }}>
            <div style={{ display: "flex", justifyContent: "center", gap: "24px", fontSize: "14px", color: COLORS.textSecondary }}>
              <span>Sessions completed: <span style={{ fontWeight: "600", color: COLORS.text }}>{sessionNumber - 1}{treatment.planned_sessions ? ` of ${treatment.planned_sessions}` : ""}</span></span>
              <span>This is session: <span style={{ fontWeight: "600", color: COLORS.text }}>#{sessionNumber}</span></span>
            </div>
          </div>

        </div>

        {/* ═══════ Right Column: Telemetry + Loaded Protocol + Countdown + Prescribed Parameters ═══════ */}
        <div>
          {/* Device Telemetry — matches MT Determination screen style */}
          <div style={{ background: "#1A1F2E", borderRadius: "12px", padding: "20px", marginBottom: "16px", color: "rgba(255,255,255,0.5)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
              <div style={{ fontSize: "12px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px" }}>Telemetry</div>
              {deviceConnected && (
                <span style={{ fontSize: "10px", padding: "2px 8px", borderRadius: "4px",
                  background: deviceSimulated ? "rgba(52,152,219,0.2)" : "rgba(46,204,113,0.2)",
                  color: deviceSimulated ? "#3498DB" : "#2ECC71" }}>
                  {deviceSimulated ? "Simulated" : "Live"}
                </span>
              )}
            </div>

            {/* Coil Mismatch Warning */}
            {coilMismatch && !mismatchAcknowledged && (
              <div style={{ background: "rgba(243,156,18,0.15)", border: "1px solid rgba(243,156,18,0.4)", borderRadius: "8px", padding: "10px 14px", marginBottom: "12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: "12px", fontWeight: "700", color: "#F39C12" }}>⚠️ Coil Mismatch</div>
                  <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.6)", marginTop: "2px" }}>
                    Prescribed: {telemetry?.prescribed_coil} · Detected: {telemetry?.detected_coil}
                  </div>
                </div>
                <button onClick={() => { setMismatchAcknowledged(true); setCoilMismatch(false); }}
                  style={{ padding: "4px 12px", borderRadius: "6px", border: "1px solid rgba(243,156,18,0.4)", background: "transparent", color: "#F39C12", fontSize: "11px", fontWeight: "600", cursor: "pointer" }}>
                  Acknowledge
                </button>
              </div>
            )}

            {/* High Temp Warning */}
            {telemetry?.coil_temp_status === "red" && (
              <div style={{ background: "rgba(231,76,60,0.15)", border: "1px solid rgba(231,76,60,0.4)", borderRadius: "8px", padding: "10px 14px", marginBottom: "12px" }}>
                <div style={{ fontSize: "12px", fontWeight: "700", color: "#E74C3C" }}>🔴 Coil temperature critical: {telemetry.coil_temp_c}°C</div>
                <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.6)", marginTop: "2px" }}>Consider pausing treatment</div>
              </div>
            )}

            {/* Device Error */}
            {deviceError && (
              <div style={{ background: "rgba(231,76,60,0.15)", border: "1px solid rgba(231,76,60,0.4)", borderRadius: "8px", padding: "10px 14px", marginBottom: "12px" }}>
                <div style={{ fontSize: "12px", color: "#E74C3C" }}>{deviceError}</div>
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "12px" }}>
              <div style={{ padding: "12px", background: "rgba(255,255,255,0.05)", borderRadius: "8px",
                borderLeft: telemetry?.coil_temp_status === "red" ? "3px solid #E74C3C" : telemetry?.coil_temp_status === "yellow" ? "3px solid #F39C12" : "3px solid transparent" }}>
                <div style={{ fontSize: "10px", textTransform: "uppercase", marginBottom: "4px" }}>Coil Temp</div>
                <div style={{ fontSize: "24px", fontWeight: "700",
                  color: !deviceConnected ? "rgba(255,255,255,0.3)"
                    : telemetry?.no_coil_flow ? "#3498DB"
                    : telemetry?.coil_temp_status === "red" ? "#E74C3C"
                    : telemetry?.coil_temp_status === "yellow" ? "#F39C12"
                    : "#2ECC71" }}>
                  {deviceConnected && telemetry
                    ? (telemetry.no_coil_flow ? "No Flow" : telemetry.coil_temp_c != null ? `${telemetry.coil_temp_c}°C` : "--°C")
                    : "--°C"}
                </div>
              </div>
              <div style={{ padding: "12px", background: "rgba(255,255,255,0.05)", borderRadius: "8px" }}>
                <div style={{ fontSize: "10px", textTransform: "uppercase", marginBottom: "4px" }}>Amplitude</div>
                <div style={{ fontSize: "24px", fontWeight: "700", color: deviceConnected && telemetry ? "#FFF" : "rgba(255,255,255,0.3)" }}>
                  {deviceConnected && telemetry?.amplitude_pct != null ? `${telemetry.amplitude_pct}%` : "--%"}
                </div>
              </div>
              <div style={{ padding: "12px", background: "rgba(255,255,255,0.05)", borderRadius: "8px" }}>
                <div style={{ fontSize: "10px", textTransform: "uppercase", marginBottom: "4px" }}>Status</div>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <div style={{ width: "8px", height: "8px", borderRadius: "50%",
                    background: deviceConnected ? (telemetry?.device_enabled ? "#2ECC71" : "#3498DB") : "#E74C3C" }} />
                  <span style={{ fontSize: "13px", color: "rgba(255,255,255,0.7)" }}>
                    {deviceConnected ? (telemetry?.device_enabled ? "Enabled" : "Connected") : "Offline"}
                  </span>
                </div>
              </div>
              <div style={{ padding: "12px", background: "rgba(255,255,255,0.05)", borderRadius: "8px",
                borderLeft: mismatchAcknowledged ? "3px solid #F39C12" : "3px solid transparent" }}>
                <div style={{ fontSize: "10px", textTransform: "uppercase", marginBottom: "4px" }}>Coil Type</div>
                <div style={{ fontSize: "13px", color: deviceConnected && telemetry?.coil_type ? "rgba(255,255,255,0.8)" : "rgba(255,255,255,0.3)" }}>
                  {deviceConnected && telemetry?.coil_type ? telemetry.coil_type : (treatment.treatment_coil || protocol?.treatment_coil || "—")}
                  {mismatchAcknowledged && <span style={{ fontSize: "10px", color: "#F39C12", marginLeft: "4px" }}>(mismatch)</span>}
                </div>
              </div>
            </div>

            {deviceConnected && telemetry && (
              <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.4)" }}>
                {telemetry.mode && <span>Mode: {telemetry.mode} · </span>}
                {telemetry.waveform && <span>Waveform: {telemetry.waveform}</span>}
              </div>
            )}

            {/* Recent events */}
            {deviceConnected && telemetry?.events?.length > 0 && (
              <div style={{ maxHeight: "60px", overflowY: "auto", borderTop: "1px solid rgba(255,255,255,0.1)", paddingTop: "8px", marginTop: "8px" }}>
                {telemetry.events.slice(-3).map((evt, i) => (
                  <div key={i} style={{ fontSize: "10px", color: "rgba(255,255,255,0.4)", marginBottom: "2px", fontFamily: "monospace" }}>
                    {evt.timestamp?.split("T")[1]?.substring(0, 8)} {evt.message}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Countdown Timer + Trains Remaining — visible during ACTIVE,
              PAUSED, and RESUMING. During PAUSED/RESUMING the clock is
              frozen (timer effect only ticks in ACTIVE), so the display
              shows the last ACTIVE value. */}
          {(phase === "ACTIVE" || phase === "PAUSED" || phase === "RESUMING") && (
            <div style={{ background: COLORS.surface, borderRadius: "12px", border: `1px solid ${COLORS.borderLight}`, padding: "20px", marginBottom: "16px", textAlign: "center", opacity: phase === "ACTIVE" ? 1 : 0.75 }}>
              {(phase === "PAUSED" || phase === "RESUMING") && (
                <div style={{ fontSize: "11px", fontWeight: "700", color: COLORS.warning, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "8px" }}>
                  ❚❚ Clock paused
                </div>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "12px" }}>
                <div>
                  <div style={{ fontSize: "11px", fontWeight: "600", color: COLORS.textSecondary, textTransform: "uppercase", marginBottom: "6px" }}>Time Remaining</div>
                  <div style={{ fontSize: "38px", fontWeight: "700", color: remainingSec < 60 && phase === "ACTIVE" ? COLORS.accent : COLORS.text, fontVariantNumeric: "tabular-nums" }}>{formatTime(remainingSec)}</div>
                </div>
                <div>
                  <div style={{ fontSize: "11px", fontWeight: "600", color: COLORS.textSecondary, textTransform: "uppercase", marginBottom: "6px" }}>Trains Remaining</div>
                  <div style={{ fontSize: "38px", fontWeight: "700", color: trainsRemaining <= 1 && phase === "ACTIVE" ? COLORS.accent : COLORS.text, fontVariantNumeric: "tabular-nums" }}>{trainsRemaining}</div>
                  <div style={{ fontSize: "11px", color: COLORS.textSecondary }}>of {protocol?.num_trains}</div>
                </div>
              </div>
              <div style={{ height: "8px", background: COLORS.borderLight, borderRadius: "4px", overflow: "hidden", margin: "8px 0" }}>
                <div style={{ height: "100%", width: `${Math.min(100, totalDuration > 0 ? (elapsedSec / totalDuration) * 100 : 0)}%`, background: phase === "ACTIVE" ? COLORS.accent : COLORS.warning, borderRadius: "4px", transition: "width 1s linear" }} />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", color: COLORS.textSecondary }}>
                <span>Elapsed: {formatTime(elapsedSec)}</span>
                <span>{totalDuration > 0 ? `${Math.round((elapsedSec / totalDuration) * 100)}%` : "—"}</span>
              </div>
            </div>
          )}

          {/* Prescribed Parameters — reference info */}
          <div style={{ background: COLORS.surface, borderRadius: "12px", border: `1px solid ${COLORS.borderLight}`, padding: "20px" }}>
            <div style={{ fontSize: "12px", fontWeight: "700", color: COLORS.textSecondary, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "12px" }}>Prescribed Parameters</div>
            {paramRow("Protocol", treatment.protocol_name)}
            {paramRow("Type", protocol?.treatment_type === "TBS" ? "Theta Burst" : protocol?.treatment_type === "MPNS" ? "mPNS" : "rTMS")}
            {paramRow("Frequency", `${protocol?.frequency_hz} Hz`)}
            {paramRow("Train Pulses", protocol?.train_pulses)}
            {paramRow("# Trains", protocol?.num_trains)}
            {paramRow("ITI", `${protocol?.inter_train_interval_sec}s`)}
            {paramRow("Total Pulses", prescribedPulses.toLocaleString())}
            {paramRow("Duration", formatDuration(totalDuration))}
            {paramRow("Treatment Coil", treatment.treatment_coil || protocol?.treatment_coil)}
            {paramRow("Treatment Spot", treatment.treatment_spot || protocol?.treatment_spot)}
            {paramRow("Intensity Ratio", (() => {
              const unit = protocol?.intensity_is_machine_pct ? "machine" : "MT";
              const isCustom = effectiveTxRatio !== (protocol?.treatment_ratio_pct || 120);
              return isCustom
                ? `${effectiveTxRatio}% ${unit} (custom; protocol default ${protocol?.treatment_ratio_pct || 120}%)`
                : `${effectiveTxRatio}% ${unit}`;
            })())}
            {paramRow("CPT Code", cptCode)}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Reminder Form ───────────────────────────────────────────

